// ФТУЕ.6: хук авто-запуска tooltip-флоу при первом mount экрана.
//
// Использование:
//   useTooltipFlow('shop', () => [
//     { id: 's1', targetSel: '[data-tt="shop-pack-osnovnoy"]',
//       spotlight: true, accent: 'cy',
//       coach: { title: '...', body: '...', showNext: true } },
//     { id: 's2', ... }
//   ]);
//
// Логика:
//   1. Если ftueTooltipsSeen.has(code) — ничего не делаем (повторный вход).
//   2. Если ftueStatus === 'pending' — игрок ещё в Pre-FTUE → ждём.
//   3. Иначе на mount стартуем шаг 0; коллбэки onNext/onSkip переключают
//      шаги. После последнего шага (или skip) — markFtueTooltipSeen(code)
//      на сервер + в store, флоу больше не запустится.
//
// Хук безопасен к повторному рендеру — флоу стартует только если
// конкретный шаг не активен сейчас (проверка через useRef).

import { useEffect, useRef } from 'react';
import { useGameStore } from '../store/useGameStore';
import { useTutorialStore, type TutorialStep } from './store';
import { markFtueTooltipSeen } from '../api/client';

export function useTooltipFlow(code: string, getSteps: () => TutorialStep[]) {
  const seen        = useGameStore(g => g.ftueTooltipsSeen);
  const ftueStatus  = useGameStore(g => g.ftueStatus);
  const markLocal   = useGameStore(g => g.markFtueTooltipSeenLocal);
  const serverSync  = useGameStore(g => g.serverSynced);
  const startTutorial = useTutorialStore(t => t.start);
  const stopTutorial  = useTutorialStore(t => t.stop);

  // Чтобы не стартовать второй раз при ре-рендере или strict-mode-двойном
  // mount: запоминаем что для этого code уже запустили.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    if (!serverSync) return;
    // Pre-FTUE не закончен → ждём.
    if (ftueStatus === 'pending' || ftueStatus === 'in_progress') return;
    if (seen.has(code)) return;

    const steps = getSteps();
    if (steps.length === 0) {
      // Шагов нет — отмечаем как просмотренное, чтобы не дёргать снова.
      markLocal(code);
      markFtueTooltipSeen(code).catch(() => { /* offline */ });
      return;
    }

    startedRef.current = true;

    let i = 0;
    const runStep = (idx: number) => {
      if (idx >= steps.length) {
        // Финиш — серверу + локально.
        markLocal(code);
        markFtueTooltipSeen(code).catch(() => { /* offline */ });
        stopTutorial();
        return;
      }
      startTutorial(steps[idx], {
        onNext: () => { i = idx + 1; runStep(i); },
        onSkip: () => {
          // Skip — пропускаем весь флоу. Помечаем как просмотренное
          // чтобы не назойничать. Игрок может перезапустить из
          // настроек Профиля (отдельная кнопка позже).
          markLocal(code);
          markFtueTooltipSeen(code).catch(() => { /* offline */ });
          stopTutorial();
        }
      });
    };
    runStep(0);

    return () => {
      // На unmount — если ещё активны, останавливаем.
      stopTutorial();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverSync, ftueStatus, seen, code]);
}
